import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { QueryResult } from '@apollo/client';
import { NewSection } from '../NewSection';
import { AdsTitle, AdsTitleCount, SectionRightBlock } from './styled';
import { PriceViewToggle } from '../NewSearch/NewSearchBar/PriceViewToggle';
import { MapButton } from '../NewSearch/NewSearchBar/MapButton';
import { SortingFilter } from '../NewSearch/NewSearchBar/SortingFilter';
import { PriceViewMode, TransformedObj } from './types';
import { ItemConnectionSort } from '../../graphql/types';
import { ItemSectionSkeleton } from './ItemSectionSkeleton';
import { ItemCard } from '../ui/ItemCard';
import { LotriverSearchResultBanner } from '../Banner/LotriverSearchResultBanner';
import useScreenSize from '../../hooks/useScreenSize';
import { useClient } from '../../hooks/useClient';
import { useBookmarks } from '../../hooks/useBookmarks';
import { PriceViewI, useInitialPriceView } from '../../hooks/usePriceView';
import { checkIsCategorySupportsPriceToggle } from '../../utils/helpers/common';
import { selectSearchFormCurrentState } from '../../store/selectors';
import { withEndlessScroll } from '../HOC/endlessScroll';
import { CardsLayout } from '../ui/CardsLayout';
import { SearchItemsQuery } from '../../graphql/graphql';

const searchListRef = React.createRef();
const EndlessSearchItems = withEndlessScroll(CardsLayout);

const getBannerInterval = (isSmall: boolean, isLarge: boolean) => {
  if (isSmall) return 5;
  if (isLarge) return 11;
  return 8;
};

interface RegularItemsSectionRightBlockProps extends PriceViewI {
  isItemsAllPage: boolean;
  isShowToggle: boolean;
}

interface RegularItemsSectionProps {
  transformedObj: TransformedObj;
  searchQuery: QueryResult<SearchItemsQuery>;
  isItemsAllPage: boolean;
}

const RegularItemsSectionRightBlock: React.FC<
  RegularItemsSectionRightBlockProps
> = ({
  isItemsAllPage,
  isShowToggle,
  priceViewOptions,
  priceView,
  setPriceView
}) => {
  const { t } = useTranslation();

  const formattedOptions = useMemo(
    () =>
      priceViewOptions.map(option => ({
        id: option.id,
        name: t(option.translationKey)
      })),
    [priceViewOptions, t]
  );

  return (
    <SectionRightBlock>
      {isShowToggle && (
        <PriceViewToggle
          options={formattedOptions}
          selectedValue={priceView}
          onChange={setPriceView}
        />
      )}
      {!isItemsAllPage && <MapButton />}
      <SortingFilter
        submitOnChange
        isPerUnit={priceView === PriceViewMode.PerAcre}
      />
    </SectionRightBlock>
  );
};

export const RegularItemsSection: React.FC<RegularItemsSectionProps> = ({
  transformedObj,
  searchQuery,
  isItemsAllPage
}) => {
  const { isSmall, isLarge } = useScreenSize();
  const isClient = useClient();
  const { t } = useTranslation();
  const { isBookmarked } = useBookmarks();
  const currentFormState = useSelector(selectSearchFormCurrentState);
  const isShowToggle = checkIsCategorySupportsPriceToggle(currentFormState);

  const bannerInterval = useMemo(
    () => getBannerInterval(isSmall, isLarge),
    [isSmall, isLarge]
  );

  const priceViewObj = useInitialPriceView(
    transformedObj.filter?.categoryId || null,
    transformedObj.sorting as ItemConnectionSort
  );

  const itemsList = useMemo(() => {
    const edges = searchQuery?.data?.itemsConnection?.edges || [];
    return edges.map((item, currIndex) => (
      <React.Fragment key={item?.node.id}>
        <ItemCard
          {...item?.node}
          inBookmarks={isBookmarked(item?.node.id)}
          priceView={priceViewObj.priceView}
        />
        {isClient && (
          <LotriverSearchResultBanner
            currIndex={currIndex}
            count={bannerInterval}
          />
        )}
      </React.Fragment>
    ));
  }, [
    searchQuery?.data,
    isBookmarked,
    priceViewObj.priceView,
    isClient,
    bannerInterval
  ]);

  const handleLoadMore = useCallback(() => {
    const pageInfo = searchQuery?.data?.itemsConnection?.pageInfo;
    if (!pageInfo?.hasNextPage || searchQuery?.loading) return;

    searchQuery.fetchMore({
      variables: {
        first: 16,
        filter: transformedObj.filter,
        sort: transformedObj.sorting || ItemConnectionSort.BumpedAtDesc,
        cursor: pageInfo.endCursor
      }
    });
  }, [searchQuery, transformedObj]);

  if (searchQuery?.loading && !itemsList.length) {
    return <ItemSectionSkeleton />;
  }

  const totalCount = searchQuery?.data?.itemsConnection?.totalCount || 0;

  const title = (
    <AdsTitle tag="h3">
      {t(isItemsAllPage ? 'search.items_all_ads' : 'search.ads')}
      <AdsTitleCount tag="body_1">({totalCount})</AdsTitleCount>
    </AdsTitle>
  );

  const rightBlock = (
    <RegularItemsSectionRightBlock
      {...{
        isItemsAllPage,
        isShowToggle,
        ...priceViewObj
      }}
    />
  );

  return (
    <NewSection
      id="search-page-regular-items"
      title={title}
      headlineType="h3"
      cypress="search-page-regular-items-title"
      rightBlock={rightBlock}
      stackOnMobile={isShowToggle}
    >
      <EndlessSearchItems
        items={itemsList}
        loading={searchQuery?.loading}
        ref={searchListRef}
        isStopLoad={!searchQuery?.data?.itemsConnection?.pageInfo?.hasNextPage}
        onLoadMore={handleLoadMore}
      >
        {itemsList}
      </EndlessSearchItems>
    </NewSection>
  );
};
